import { useCallback, useRef, useState } from 'react'
import type {
  FormEvent,
  KeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  Ref,
} from 'react'
import clsx from 'clsx'
import { PageSidePanel } from './PageSidePanels'
import SidePanelResizeHandle from './SidePanelResizeHandle'

export const DOCKED_PANEL_DEFAULT_WIDTH_PX = 400
export const DOCKED_PANEL_MIN_WIDTH_PX = 320
const DOCKED_PANEL_MAX_WIDTH_PX = 960

/** Each docked dialog's width, by title, so pinning it again opens it as wide as it was. */
const dockedWidths = new Map<string, number>()

/**
 * A dialog pinned beside the page: a card on the frame's right (with Record
 * Preview and the other end panels), resizable from its left edge. The page
 * stays usable; Escape inside it closes it as its ✕ does.
 */
export function DockedModalPanel({
  title,
  titleId,
  onClose,
  header,
  children,
  footer,
  bare = false,
  defaultWidth = DOCKED_PANEL_DEFAULT_WIDTH_PX,
  widthKey,
  contentClassName,
  footerClassName,
  contentRef,
  onSubmit,
}: {
  title: string
  titleId: string
  onClose: () => void
  header?: ReactNode
  children: ReactNode
  footer?: ReactNode
  /**
   * The dialog draws its own header, scrolling body and footer (Field
   * Settings), so the panel only frames it: no padding or scroll of its own.
   */
  bare?: boolean
  /** Its width the first time it's pinned; after that, as wide as it was left. */
  defaultWidth?: number
  /** What its remembered width is kept under, when not its title (every field's Field Settings). */
  widthKey?: string
  /** The content's own padding and scrollbar, replacing the panel's. */
  contentClassName?: string
  /** The footer's own border color and padding, replacing the panel's. */
  footerClassName?: string
  contentRef?: Ref<HTMLDivElement>
  /** Header, content and footer are one form (ModalShell's `onSubmit`). */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void
}) {
  const panelRef = useRef<HTMLElement | null>(null)
  const sizeKey = widthKey ?? title
  const [width, setWidth] = useState(() => dockedWidths.get(sizeKey) ?? defaultWidth)
  const [isResizing, setIsResizing] = useState(false)

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return
    // A popover open in the panel takes this Escape; the next one is the panel's.
    if (document.querySelector('[data-escape-consumer="popover"]')) return
    event.preventDefault()
    onClose()
  }

  const handleResizeStart = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return
      const startWidth = panelRef.current?.getBoundingClientRect().width ?? width
      const startX = event.clientX
      const previousCursor = document.body.style.cursor
      const previousUserSelect = document.body.style.userSelect
      event.preventDefault()
      setIsResizing(true)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const maxWidth = Math.max(
          DOCKED_PANEL_MIN_WIDTH_PX,
          Math.min(DOCKED_PANEL_MAX_WIDTH_PX, Math.floor(window.innerWidth * 0.6))
        )
        const next = Math.min(
          maxWidth,
          Math.max(DOCKED_PANEL_MIN_WIDTH_PX, startWidth + (startX - moveEvent.clientX))
        )
        dockedWidths.set(sizeKey, next)
        setWidth(next)
      }
      const handlePointerUp = () => {
        setIsResizing(false)
        document.body.style.cursor = previousCursor
        document.body.style.userSelect = previousUserSelect
        window.removeEventListener('pointermove', handlePointerMove)
        window.removeEventListener('pointerup', handlePointerUp)
        window.removeEventListener('pointercancel', handlePointerUp)
      }
      window.addEventListener('pointermove', handlePointerMove)
      window.addEventListener('pointerup', handlePointerUp)
      window.addEventListener('pointercancel', handlePointerUp)
    },
    [sizeKey, width]
  )

  const sections = (
    <>
      {header}
      {/* A size container, so a panel's content can change its layout when
          it's pinned narrow (`@container docked-panel`). */}
      {bare ? (
        <div className="docked-modal-panel-content flex min-h-0 flex-1 flex-col rounded-[inherit]">
          {children}
        </div>
      ) : (
        <div
          ref={contentRef}
          data-modal-content
          className={clsx(
            'docked-modal-panel-content min-h-0 flex-1 overflow-y-auto',
            contentClassName ?? 'sidebar-scrollbar px-4 py-3'
          )}
        >
          {children}
        </div>
      )}
      {footer ? (
        <div
          data-modal-footer
          className={clsx(
            'flex-none rounded-b-[inherit] border-t',
            footerClassName ?? 'border-default px-4 py-3'
          )}
        >
          {footer}
        </div>
      ) : null}
    </>
  )

  return (
    <PageSidePanel side="end">
      <aside
        ref={panelRef}
        aria-labelledby={titleId}
        data-docked-panel
        onKeyDown={handleKeyDown}
        className="docked-modal-panel app-frame-panel-end relative flex min-w-0 shrink-0 flex-col surface-default"
        style={{ width }}
      >
        <SidePanelResizeHandle
          edge="start"
          label={`Resize ${title}`}
          isResizing={isResizing}
          onPointerDown={handleResizeStart}
          layer="overlay"
        />
        {onSubmit ? (
          // `contents`, so header, content and footer stay the panel's flex column.
          <form className="contents" onSubmit={onSubmit}>
            {sections}
          </form>
        ) : (
          sections
        )}
      </aside>
    </PageSidePanel>
  )
}
